import React, { useEffect, useRef } from "react";
import { Box, FormLabel, GlobalStyles } from "@mui/material";
import { loadGoogleMaps } from "utils/loadGoogleMaps";

export type SelectedAddress = {
  street_address: string;
  city: string;
  state: string;
  post_code: string;
  country: string;
  place_id: string;
  formatted_address: string;
  latitude?: number;
  longitude?: number;
};

type Props = {
  label: string;
  value: string;
  required?: boolean;
  onChange: (value: string) => void;
  onAddressSelected: (address: SelectedAddress) => void;
  onSelectionValidChange?: (isValid: boolean) => void;
};

type GooglePlaceAutocompleteElement = HTMLElement & {
  includedRegionCodes?: string[];
  placeholder?: string;
};

const AddressAutocomplete: React.FC<Props> = ({
  label,
  required,
  onChange,
  onAddressSelected,
  onSelectionValidChange,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetRef = useRef<GooglePlaceAutocompleteElement | null>(null);

  /*
   * Store callbacks in refs so that the Google widget does not get
   * destroyed/re-created whenever AddEntityPopup re-renders.
   */
  const onChangeRef = useRef(onChange);
  const onAddressSelectedRef = useRef(onAddressSelected);
  const onSelectionValidChangeRef = useRef(onSelectionValidChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onAddressSelectedRef.current = onAddressSelected;
  }, [onAddressSelected]);

  useEffect(() => {
    onSelectionValidChangeRef.current = onSelectionValidChange;
  }, [onSelectionValidChange]);

  useEffect(() => {
    let mounted = true;
    let selectListener: EventListener | null = null;

    const init = async () => {
      try {
        await loadGoogleMaps();

        if (!mounted) {
          return;
        }

        /*
         * Get the Places library directly from importLibrary rather than
         * relying on google.maps.places having already been populated.
         */
        const placesLibrary =
          (await google.maps.importLibrary(
            "places"
          )) as google.maps.PlacesLibrary;

        if (!mounted || !containerRef.current) {
          return;
        }

        const PlaceAutocompleteElement =
          (placesLibrary as any).PlaceAutocompleteElement;

        if (!PlaceAutocompleteElement) {
          throw new Error(
            "Google PlaceAutocompleteElement is unavailable. " +
              "Check that Places API (New) is enabled for this API key."
          );
        }

        /*
         * Clear any old widget before creating this one.
         */
        containerRef.current.innerHTML = "";

        const widget =
          new PlaceAutocompleteElement() as GooglePlaceAutocompleteElement;

        widget.includedRegionCodes = [
          "gb",
          "us",
          "au",
          "ca",
        ];

        widget.placeholder = "Start typing address…";

        containerRef.current.appendChild(widget);

        widgetRef.current = widget;

        selectListener = async (event: Event) => {
          try {
            const customEvent = event as CustomEvent<{
              placePrediction?: google.maps.places.PlacePrediction;
            }>;

            const placePrediction =
              customEvent.detail?.placePrediction ??
              (event as any).placePrediction;

            if (!placePrediction) {
              console.warn(
                "Google autocomplete selection contained no placePrediction.",
                event
              );

              return;
            }

            const place = placePrediction.toPlace();

            await place.fetchFields({
              fields: [
                "id",
                "formattedAddress",
                "location",
                "addressComponents",
              ],
            });

            const components =
              place.addressComponents ?? [];

            const get = (
              type: string,
              useShort = false
            ) => {
              const component = components.find(
                (component: any) =>
                  component.types?.includes(type)
              );

              if (!component) {
                return "";
              }

              return useShort
                ? component.shortText || ""
                : component.longText || "";
            };

            const subpremise = get("subpremise");
            const premise = get("premise");
            const streetNumber = get("street_number");
            const route = get("route");

            const streetLine = [
              subpremise
                ? `Flat ${subpremise}`
                : "",
              premise,
              streetNumber,
              route,
            ]
              .filter(Boolean)
              .join(" ")
              .replace(/\s+/g, " ")
              .trim();

            const city =
              get("postal_town") ||
              get("locality") ||
              get("administrative_area_level_3") ||
              get("sublocality") ||
              "";

            const state =
              get("administrative_area_level_2") ||
              get("administrative_area_level_1") ||
              "";

            const formattedAddress =
              place.formattedAddress || "";

            const selected: SelectedAddress = {
              street_address:
                streetLine || formattedAddress,

              city,

              state,

              post_code: get("postal_code"),

              country: get("country"),

              place_id: place.id || "",

              formatted_address:
                formattedAddress,

              latitude:
                place.location?.lat(),

              longitude:
                place.location?.lng(),
            };

            onChangeRef.current(
              formattedAddress
            );

            onSelectionValidChangeRef.current?.(
              true
            );

            onAddressSelectedRef.current(
              selected
            );
          } catch (error) {
            console.error(
              "Error selecting Google place:",
              error
            );

            onSelectionValidChangeRef.current?.(
              false
            );
          }
        };

        widget.addEventListener(
          "gmp-select",
          selectListener
        );

        console.log(
          "Google address autocomplete initialised."
        );
      } catch (error) {
        console.error(
          "Failed to initialise address autocomplete:",
          error
        );
      }
    };

    init();

    return () => {
      mounted = false;

      if (
        widgetRef.current &&
        selectListener
      ) {
        widgetRef.current.removeEventListener(
          "gmp-select",
          selectListener
        );
      }

      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }

      widgetRef.current = null;
    };
  }, []);

  return (
    <>
      {/*
       * Google can render autocomplete UI outside the immediate component
       * hierarchy. MUI Dialog has a z-index around 1300, so ensure Google's
       * suggestion UI appears above it.
       */}
      <GlobalStyles
        styles={{
          ".pac-container": {
            zIndex: "1600 !important",
          },

          "gmp-place-autocomplete": {
            position: "relative",
            zIndex: 1601,
          },
        }}
      />

      <Box
        sx={{
          width: "100%",
          overflow: "visible",
          position: "relative",
          zIndex: 1601,
        }}
      >
        <FormLabel
          required={required}
          sx={{
            display: "block",
            mb: 1,
          }}
        >
          {label}
        </FormLabel>

        <Box
          ref={containerRef}
          sx={{
            width: "100%",
            overflow: "visible",
            position: "relative",

            "& gmp-place-autocomplete": {
              display: "block",
              width: "100%",
              position: "relative",
              zIndex: 1601,
            },
          }}
        />
      </Box>
    </>
  );
};

export default AddressAutocomplete;